/* eslint-disable no-unused-vars */
import PropTypes from "prop-types";
import React, { useState } from "react";
import {
  AppBar,
  Toolbar,
  Grid,
  Typography,
  Menu,
  MenuItem,
  IconButton,
  Hidden,
} from "@material-ui/core";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import MenuIcon from "@material-ui/icons/Menu";
import { Person } from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";
import { useDispatch, useSelector } from "react-redux";
import ProfilePic from "../../assets/images/profilepic.png";
import logoutAndClearData from "../../utils/utils";
import { logoutUser } from "../../redux/features/auth/actions";

// Constants
const DRAWER_WIDTH = 240;
const MINI_DRAWER_WIDTH = 60;

// Separated styles by logical sections
const useStyles = makeStyles((theme) => ({
  // App bar styles
  appBar: {
    boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.12)",
    backgroundColor: "white",
    [theme.breakpoints.up("sm")]: {
      boxShadow: "none",
      width: (props) =>
        `calc(100% - ${
          props.isCollapsed ? MINI_DRAWER_WIDTH : DRAWER_WIDTH
        }px)`,
      marginLeft: (props) =>
        props.isCollapsed ? MINI_DRAWER_WIDTH : DRAWER_WIDTH,
      transition: theme.transitions.create(["width", "margin"], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
      }),
    },
  },

  // Typography styles
  appName: {
    background: "#151D48",
    backgroundClip: "text",
    color: "transparent",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    fontWeight: "bold",
    paddingLeft: theme.spacing(2),
    fontSize: "2rem",

    [theme.breakpoints.down("md")]: {
      fontSize: "1.5rem",
    },
    [theme.breakpoints.down("sm")]: {
      paddingLeft: 0,
      fontSize: "1rem",
    },
  },

  // Layout styles
  contentContainer: {
    marginTop: theme.spacing(1),
    // marginBottom: theme.spacing(1),
    [theme.breakpoints.down("xs")]: {
      marginTop: 50,
    },
  },
  userSection: {
    display: "flex",
    justifyContent: "flex-end",
    paddingRight: "20px !important",
    [theme.breakpoints.down("sm")]: {
      paddingRight: 20,
    },
  },

  // Menu button styles
  menuButton: {
    color: "#151D48",
    marginRight: 0,
    [theme.breakpoints.up("sm")]: {
      display: "none",
    },
  },

  // User profile styles
  userContainer: {
    backgroundColor: "#EDF0F8",
    borderRadius: 100,
    display: "flex",
    padding: theme.spacing(1.2),
    alignItems: "center",
    gap: theme.spacing(1.2),
    [theme.breakpoints.down("md")]: {
      gap: theme.spacing(0.8),
    },
    [theme.breakpoints.down("sm")]: {
      gap: theme.spacing(0.4),
    },
  },
  profileBox: {
    borderRadius: 100,
    overflow: "hidden",
    display: "flex",
    alignItems: "center",
    justifyContent: "stretch",
    background: "lightgrey",
    padding: theme.spacing(0.6), // default for large screens
    boxShadow: "0px 0px 4px rgba(0, 0, 0, 0.05)",

    [theme.breakpoints.down("md")]: {
      padding: theme.spacing(0.4),
    },
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(0.3),
    },
  },

  profilePic: {
    width: 48,
    height: 48,
    objectFit: "cover",
    borderRadius: 0, // change to "50%" if you want round pics

    [theme.breakpoints.down("md")]: {
      width: 40,
      height: 40,
    },
    [theme.breakpoints.down("sm")]: {
      width: 32,
      height: 32,
    },
  },
  svgicon: {
    width: 24,
    height: 24,

    [theme.breakpoints.down("md")]: {
      width: 20,
      height: 20,
    },
    [theme.breakpoints.down("sm")]: {
      width: 16,
      height: 16,
    },
  },

  userInfo: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    lineHeight: 0.5,
  },
  userName: {
    fontSize: "0.8rem",
    color: "#1e1e1e",
    lineHeight: "100%",
    [theme.breakpoints.up("sm")]: {
      fontSize: "0.6rem",
    },
    [theme.breakpoints.up("md")]: {
      fontSize: "0.78rem",
    },
  },
  userRole: {
    fontSize: "0.5rem",
    [theme.breakpoints.up("md")]: {
      fontSize: "0.65rem",
    },
    textTransform: "capitalize",
    color: "#8c8f99",
  },

  dropdownArrow: {
    color: "#555",
  },
}));

// Component for the user profile section
const UserProfile = ({
  classes,
  handleMenuOpen,
  anchorEl,
  handleMenuClose,
  handleLogout,
}) => {
  const { user } = useSelector((state) => state.auth);

  return (
    <div className={classes.userContainer}>
      <div className={classes.profileBox}>
        {/* <img src={ProfilePic} alt="Profile" className={classes.profilePic} />
         */}
        <Person className={classes.svgicon} />
      </div>

      <div className={classes.userInfo}>
        <span className={classes.userName}>
          <strong>{user && user.name}</strong>
        </span>
        <br />
        <span className={classes.userRole}>{user && user.roles}</span>
      </div>

      <IconButton
        className={classes.dropdownArrow}
        onClick={handleMenuOpen}
        size="small"
        aria-label="User menu"
      >
        <KeyboardArrowDownIcon className={classes.svgicon} />
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        getContentAnchorEl={null}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <MenuItem onClick={handleLogout}>Logout</MenuItem>
      </Menu>
    </div>
  );
};

UserProfile.propTypes = {
  classes: PropTypes.object.isRequired,
  handleMenuOpen: PropTypes.func.isRequired,
  anchorEl: PropTypes.object,
  handleMenuClose: PropTypes.func.isRequired,
  handleLogout: PropTypes.func.isRequired,
};

// Mobile AppBar component
const MobileAppBar = ({ classes, currentTitle, handleDrawerToggle }) => (
  <Hidden smUp implementation="css">
    <AppBar position="fixed" className={classes.appBar}>
      <Toolbar>
        <IconButton
          color="inherit"
          aria-label="open drawer"
          edge="start"
          onClick={handleDrawerToggle}
          className={classes.menuButton}
        >
          <MenuIcon />
        </IconButton>
        <Grid
          container
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={2}
        >
          <Grid item xs={10} sm={8} md={7}>
            <Typography variant="h5" className={classes.appName}>
              {currentTitle}
            </Typography>
          </Grid>
        </Grid>
      </Toolbar>
    </AppBar>
  </Hidden>
);

MobileAppBar.propTypes = {
  classes: PropTypes.object.isRequired,
  currentTitle: PropTypes.string.isRequired,
  handleDrawerToggle: PropTypes.func.isRequired,
};

// Desktop AppBar component
const DesktopAppBar = ({ classes, currentTitle, userProfileProps }) => (
  <Hidden xsDown implementation="css">
    <AppBar position="absolute" className={classes.appBar}>
      <Toolbar style={{ minHeight: "unset" }}>
        <Grid
          container
          spacing={2}
          alignItems="center"
          className={classes.contentContainer}
        >
          <Grid item xs={12} sm={7} md={8} container>
            <Typography variant="h3" className={classes.appName}>
              {currentTitle}
            </Typography>
          </Grid>

          <Grid
            item
            xs={12}
            sm={5}
            md={4}
            container
            justify="flex-end"
            className={classes.userSection}
          >
            <UserProfile {...userProfileProps} />
          </Grid>
        </Grid>
      </Toolbar>
    </AppBar>
  </Hidden>
);

DesktopAppBar.propTypes = {
  classes: PropTypes.object.isRequired,
  currentTitle: PropTypes.string.isRequired,
  userProfileProps: PropTypes.object.isRequired,
  handleSidebarToggle: PropTypes.func.isRequired,
  isCollapsed: PropTypes.bool.isRequired,
};

const TopAppBar = ({
  currentTitle,
  handleDrawerToggle,
  handleSidebarToggle,
  isCollapsed = false,
}) => {
  const classes = useStyles({ isCollapsed });
  const [anchorEl, setAnchorEl] = useState(null);
  const dispatch = useDispatch();

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    dispatch(logoutUser());
    handleMenuClose();
  };

  // Props for the UserProfile component
  const userProfileProps = {
    classes,
    handleMenuOpen,
    anchorEl,
    handleMenuClose,
    handleLogout,
  };

  return (
    <>
      <MobileAppBar
        classes={classes}
        currentTitle={currentTitle}
        handleDrawerToggle={handleDrawerToggle}
      />
      <DesktopAppBar
        classes={classes}
        currentTitle={currentTitle}
        userProfileProps={userProfileProps}
      />
    </>
  );
};

TopAppBar.propTypes = {
  currentTitle: PropTypes.string.isRequired,
  handleDrawerToggle: PropTypes.func.isRequired,
  handleSidebarToggle: PropTypes.func.isRequired,
  isCollapsed: PropTypes.bool,
};

export default TopAppBar;
